import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import { getAllowedTeams, getTeamOptions, applyTeamFilter } from '../../utils/teamPermissions';

import { 
  ArrowLeft,
  Users,
  PlusCircle,
  Search,
  Edit2,
  Eye,
  Trash2,
  UserCircle,
  Calendar,
  Phone,
  Mail,
  Shield,
  AlertTriangle
} from 'lucide-react';

const PlayersList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTeam, setFilterTeam] = useState('Todos');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const teams = useMemo(() => getTeamOptions(userProfile), [userProfile]);

  useEffect(() => {
    if (userProfile) {
      fetchPlayers();
    }
  }, [filterTeam, userProfile]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);

      // Calcular allowedTeams DENTRO de la función
      const allowedTeams = getAllowedTeams(userProfile);

      let query = supabase
        .from('players')
        .select('*')
        .eq('active', true)
        .order('team', { ascending: true })
        .order('last_name', { ascending: true });

      query = applyTeamFilter(query, allowedTeams, filterTeam);

      const { data, error } = await query;

      if (error) throw error;
      setPlayers(data || []);
    } catch (error) {
      console.error('Error al cargar jugadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!playerToDelete) return;

    try {
      setDeleting(true);

      // Marcar como inactivo en lugar de eliminar
      const { error } = await supabase
        .from('players')
        .update({ 
          active: false,
          left_date: new Date().toISOString().split('T')[0]
        })
        .eq('id', playerToDelete.id);

      if (error) throw error;

      setPlayers(players.filter(p => p.id !== playerToDelete.id));
      setShowDeleteModal(false);
      setPlayerToDelete(null);
    } catch (error) {
      console.error('Error al eliminar jugador:', error);
      alert('Error al eliminar el jugador');
    } finally {
      setDeleting(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    const searchLower = searchTerm.toLowerCase();
    return (
      player.first_name.toLowerCase().includes(searchLower) ||
      player.last_name.toLowerCase().includes(searchLower) ||
      (player.player_number && player.player_number.toString().includes(searchLower))
    );
  });

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getTeamColor = (team) => {
    switch(team) {
      case 'Juvenil':
        return 'bg-blue-100 text-blue-800';
      case 'Cadete':
        return 'bg-green-100 text-green-800';
      case 'Amateur':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPositionIcon = (position) => {
    return <Shield className="w-4 h-4" />;
  };

  // Agrupar jugadores por equipo
  const playersByTeam = filteredPlayers.reduce((acc, player) => {
    if (!acc[player.team]) {
      acc[player.team] = [];
    }
    acc[player.team].push(player);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando jugadores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Jugadores</h1>
                <p className="text-sm text-gray-600">{filteredPlayers.length} jugadores activos</p>
              </div>
            </div>

            {(userProfile?.role === 'admin' || userProfile?.role === 'entrenador') && (
              <button
                onClick={() => navigate('/players/new')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusCircle className="w-5 h-5" />
                Nuevo Jugador
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Jugadores</p>
                <p className="text-2xl font-bold text-gray-900">{players.length}</p>
              </div>
            </div>
          </div>

          {['Juvenil', 'Cadete', 'Amateur'].map(team => (
            <div key={team} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${getTeamColor(team)}`}>
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{team}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {players.filter(p => p.team === team).length}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o dorsal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filtro por equipo */}
            {userProfile?.role !== 'entrenador' && (
              <div>
                <select
                  value={filterTeam}
                  onChange={(e) => setFilterTeam(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {teams.map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Lista de jugadores por equipo */}
        {Object.keys(playersByTeam).length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay jugadores</h3>
            <p className="text-gray-600 mb-6">Comienza añadiendo el primer jugador al equipo</p>
            {(userProfile?.role === 'admin' || userProfile?.role === 'entrenador') && (
              <button
                onClick={() => navigate('/players/new')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusCircle className="w-5 h-5" />
                Añadir Jugador
              </button>
            )}
          </div>
        ) : (
          Object.entries(playersByTeam).map(([team, teamPlayers]) => (
            <div key={team} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-xl font-bold text-gray-900">{team}</h2>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                  {teamPlayers.length} jugadores
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {teamPlayers.map((player) => (
                  <div
                    key={player.id}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    <div className={`h-2 ${getTeamColor(player.team).replace('text-', 'bg-').split(' ')[0]}`}></div>
                    
                    <div className="p-6">
                      {/* Header con foto y dorsal */}
                      <div className="flex items-start gap-4 mb-4">
                        <div className="relative">
                          <PrivateImage
                            bucket="player-photos"
                            path={player.photo_url}
                            alt={`${player.first_name} ${player.last_name}`}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                          {player.player_number && (
                            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {player.player_number}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 truncate">
                            {player.first_name} {player.last_name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            {player.position && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                {getPositionIcon(player.position)}
                                {player.position}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Info del jugador */}
                      <div className="space-y-2 mb-4">
                        {player.date_of_birth && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>{calculateAge(player.date_of_birth)} años</span>
                          </div>
                        )}
                        {player.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4" />
                            <span className="truncate">{player.phone}</span>
                          </div>
                        )}
                        {player.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{player.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Acciones */}
                      <div className="flex gap-2 pt-4 border-t">
                        <button
                          onClick={() => navigate(`/players/${player.id}`)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Ver
                        </button>
                        {(userProfile?.role === 'admin' || userProfile?.role === 'entrenador') && (
                          <>
                            <button
                              onClick={() => navigate(`/players/edit/${player.id}`)}
                              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {userProfile?.role === 'admin' && (
                              <button
                                onClick={() => {
                                  setPlayerToDelete(player);
                                  setShowDeleteModal(true);
                                }}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Dar de baja jugador</h3>
                <p className="text-sm text-gray-600">El jugador será marcado como inactivo</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Jugador:</strong> {playerToDelete?.first_name} {playerToDelete?.last_name}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Equipo:</strong> {playerToDelete?.team}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPlayerToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Eliminando...' : 'Dar de baja'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayersList;